import { useState } from 'react'
import type { ActivityType, UserData } from '../../types'
import type { Period } from '../../utils/analytics'
import OverviewCharts from './OverviewCharts'
import ActivityCharts from './ActivityCharts'
import NutritionCharts from './NutritionCharts'
import PeriodToggle from './PeriodToggle'

type Tab = 'overview' | ActivityType | 'nutrition'

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'nutrition', label: 'Nutrition' },
  { key: 'cardio', label: 'Cardio' },
  { key: 'weights', label: 'Weights' },
  { key: 'exercise', label: 'Exercise' },
]

const TAB_KEYS = new Set<string>(['overview', 'cardio', 'weights', 'exercise', 'nutrition'])

function initialTab(): Tab {
  if (typeof window === 'undefined') return 'overview'
  const t = new URLSearchParams(window.location.search).get('tab')
  return t && TAB_KEYS.has(t) ? (t as Tab) : 'overview'
}

export default function Analytics({ data }: { data: UserData }) {
  const [tab, setTab] = useState<Tab>(initialTab)
  const [period, setPeriod] = useState<Period>('month')

  return (
    <div className="mt-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-lg font-bold">Analytics</h2>
        <PeriodToggle value={period} onChange={setPeriod} />
      </div>
      <div className="flex gap-1 border-b border-[var(--border)] overflow-x-auto overflow-y-hidden whitespace-nowrap">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`shrink-0 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? 'border-[var(--accent-teal)] text-[var(--accent-teal)]'
                : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' ? (
        <OverviewCharts data={data} period={period} />
      ) : tab === 'nutrition' ? (
        <NutritionCharts data={data} period={period} />
      ) : (
        <ActivityCharts data={data} type={tab} period={period} />
      )}
    </div>
  )
}
